import React, { useContext, useState, useMemo, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button as MuiButton,
} from "@mui/material";
import {
  Modal,
  ModalClose,
  Typography,
  Sheet,
  Input,
  Select,
  Option,
  Divider,
} from "@mui/joy";
import CheckIcon from "@mui/icons-material/Check";
import AuthContext from "../../contexts/authContext";
import axiosInstance from "../../api/axios";
import { toast } from "react-hot-toast"; // uncomment if you use toast
export default function Reports() {
  const [openDailyReportModal, setOpenDailyReportModal] = useState(false);
  const { userData, setRefresh } = useContext(AuthContext);
  const [openReportModal, setOpenReportModal] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [selectedUser, setSelectedUser] = useState("all");
  /** ---------- helpers ---------- */
  const todayDate = new Date().toLocaleDateString("en-GB"); // 01/05/2025

  const todaysPayments = useMemo(
    () => userData.payments?.filter((p) => p.createdAt === todayDate) || [],
    [userData.payments, todayDate]
  );

  const totalAmount = useMemo(
    () => todaysPayments.reduce((sum, p) => sum + (p.amountPaid || 0), 0),
    [todaysPayments]
  );
  useEffect(() => {
    if (!startDate || !endDate || !userData?.payments) {
      setFilteredPayments([]);
      return;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);
    end.setDate(end.getDate() + 1); // inclusive

    const result = userData.payments.filter((payment) => {
      const created = new Date(payment.createdAt);
      const isWithinRange = created >= start && created < end;
      const matchesUser =
        selectedUser === "all" || payment.agentId._id === selectedUser;

      return isWithinRange && matchesUser;
    });

    setFilteredPayments(result);
  }, [startDate, endDate, selectedUser, userData]);

  // ---------- Customer Updates ----------
  const customerUpdates = useMemo(() => {
    if (!startDate || !endDate || !userData?.customers) return [];

    const start = new Date(startDate);
    const end = new Date(endDate);
    end.setDate(end.getDate() + 1);

    let arr = [];

    userData.customers.forEach((cust) => {
      cust.updates?.forEach((u) => {
        const created = new Date(u.date);
        if (
          created >= start &&
          created < end &&
          (selectedUser === "all" || u.added_by === selectedUser)
        ) {
          arr.push({
            text: u.text,
            addedBy: userData.users.find((usr) => usr._id === u.added_by)?.name,
            date: u.date,
            customerName: cust.name,
          });
        }
      });
    });

    return arr;
  }, [startDate, endDate, userData]);

  // ---------- Asset + Design Updates ----------
  const designUpdates = useMemo(() => {
    if (!startDate || !endDate || !userData?.assets) return [];

    const start = new Date(startDate);
    const end = new Date(endDate);
    end.setDate(end.getDate() + 1);

    let arr = [];

    userData.assets.forEach((asset) => {
      asset.designs?.forEach((design) => {
        design.updates?.forEach((u) => {
          const created = new Date(u.date);
          if (
            created >= start &&
            created < end &&
            (selectedUser === "all" || u.added_by === selectedUser)
          ) {
            arr.push({
              text: u.text,
              addedBy: userData.users.find((usr) => usr._id === u.added_by)
                ?.name,
              date: u.date,
              designName: design.name,
              assetName: asset.assetName,
            });
          }
        });
      });
    });

    return arr;
  }, [startDate, endDate, userData]);

  /** ---------- actions ---------- */
  const handleReceiptDone = async (paymentId) => {
    try {
      await axiosInstance.put(`/payments/receipt/${paymentId}/`, {
        receiptReference: "created",
      });
      // flip a flag so upper component refreshes data
      setRefresh(true);
    } catch (err) {
      console.error(err);
      toast.error("Couldn’t update receipt status"); // uncomment if you use toast
    }
  };
  const handleCloseReportModal = () => {
    setStartDate("");
    setEndDate("");
    setSelectedUser("all");
    setFilteredPayments([]);
    setOpenReportModal(false);
  };

  return (
    <Box>
      <Typography
        sx={{
          fontSize: "24px",
          fontWeight: "400",
          textAlign: "center",
          mb: 2,
          color: "#800001",
        }}
      >
        Reports
      </Typography>
      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        {/* launcher button */}
        <MuiButton
          variant="contained"
          sx={{
            backgroundColor: "#800001",
            color: "#fff",
            fontSize: { xs: "14px", md: "16px" },
            borderRadius: "5px",
            px: { xs: 4, md: 10 },
            py: { xs: 2, md: 3 },
            "&:hover": { backgroundColor: "#800001", color: "#fff" },
          }}
          onClick={() => setOpenReportModal(true)}
        >
          Full Report
        </MuiButton>

        {/* <MuiButton
          variant="contained"
          sx={{
            backgroundColor: "#800001",
            color: "#fff",
            fontSize: { xs: "14px", md: "16px" },
            borderRadius: "5px",
            px: { xs: 4, md: 10 },
            py: { xs: 2, md: 3 },
            "&:hover": { backgroundColor: "#800001", color: "#fff" },
          }}
          onClick={() => setOpenDailyReportModal(true)}
        >
          Daily Report
        </MuiButton> */}
      </Box>

      {/* ----------------Daily Payment Report modal ---------------- */}
      <Modal
        aria-labelledby="daily-report-title"
        open={openDailyReportModal}
        onClose={() => setOpenDailyReportModal(false)}
        sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        <Sheet
          variant="outlined"
          sx={{
            width: { xs: "80vw", md: 800 },
            maxHeight: { xs: "80vh", md: "70vh" },
            borderRadius: "md",
            p: 3,
            boxShadow: "lg",
            bgcolor: "#FEDDDD",
            overflow: "hidden",
          }}
        >
          <ModalClose
            sx={{
              m: 1,
              "& svg": { color: "#800001" },
              "&:hover": {
                bgcolor: "#800001",
                "& svg": { color: "#fff" },
              },
            }}
          />

          {/* heading + total */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: 1,
              mb: 2,
              alignItems: "center",
              my: 2,
            }}
          >
            <Typography id="daily-report-title" sx={{ fontSize: 20 }}>
              Daily Report&nbsp;–
            </Typography>
            <Typography sx={{ fontSize: 16 }}>
              {todayDate} | &nbsp;₹{totalAmount.toLocaleString("en-IN")}
            </Typography>
          </Box>

          {/* table wrapper for mobile scroll */}
          <Box sx={{ overflowX: "auto", maxHeight: "calc(100% - 60px)" }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {["S.No", "Name", "Paid", "Agent", "Scheme", "Receipted"].map(
                    (head) => (
                      <TableCell
                        key={head}
                        align="center"
                        sx={{
                          fontWeight: 600,
                          backgroundColor: "#800001",
                          color: "#fff",
                        }}
                      >
                        {head}
                      </TableCell>
                    )
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {todaysPayments.map((pay, index) => {
                  const schemeName =
                    userData.schemes.find(
                      (s) => s._id === pay.schemeId.schemeId
                    )?.schemeName || "-";

                  const receiptDone = pay.receiptReference === "created"; // adapt to your field

                  return (
                    <TableRow key={pay._id}>
                      <TableCell align="center">{index + 1}</TableCell>
                      <TableCell align="center">
                        {pay.customerId.name}
                      </TableCell>
                      <TableCell align="center">{pay.amountPaid}</TableCell>
                      <TableCell align="center">{pay.agentId.name}</TableCell>
                      <TableCell align="center">{schemeName}</TableCell>
                      <TableCell align="center">
                        {receiptDone ? (
                          <Typography>Receipted</Typography>
                        ) : (
                          <MuiButton
                            size="small"
                            disabled={receiptDone}
                            onClick={() => handleReceiptDone(pay._id)}
                            sx={{
                              minWidth: 0,
                              p: 0.5,
                              bgcolor: receiptDone ? "#4caf50" : "#800001",
                              "&:hover": {
                                bgcolor: receiptDone ? "#4caf50" : "#800001",
                              },
                            }}
                          >
                            <CheckIcon
                              fontSize="small"
                              sx={{ color: "#fff" }}
                            />
                          </MuiButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </Sheet>
      </Modal>
      {/* ----------------Full Report modal ---------------- */}
      <Modal
        open={openReportModal}
        onClose={handleCloseReportModal}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Sheet
          sx={{
            maxWidth: 600,
            width: "100%",
            bgcolor: "#FEDDDD",
            p: 3,
            borderRadius: "md",
            boxShadow: "lg",
            m: 2,
            height: "80vh",
            overflowY: "auto",
          }}
        >
          <ModalClose
            variant="plain"
            sx={{
              m: 1,
              "& svg": { color: "#800001" },
              "&:hover": {
                backgroundColor: "#800001",
                "& svg": { color: "#fff" },
              },
            }}
          />

          <Typography level="h4" sx={{ fontWeight: "bold", mb: 1 }}>
            Filter Collections
          </Typography>

          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              fullWidth
              variant="outlined"
            />
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              fullWidth
              variant="outlined"
            />
          </Box>

          <Select
            value={selectedUser}
            onChange={(e, val) => setSelectedUser(val)}
            placeholder="Select User"
            sx={{ mb: 2 }}
          >
            <Option value="all">All Users</Option>
            {userData?.users
              ?.filter((i) => i.status !== "erased")
              .map((user) => (
                <Option key={user._id} value={user._id}>
                  {user.name}
                </Option>
              ))}
          </Select>

          <Divider sx={{ mb: 2 }} />

          {filteredPayments.length > 0 ? (
            <>
              <Typography level="body-sm" sx={{ fontWeight: "bold", mb: 1 }}>
                Total Collection: ₹
                {filteredPayments.reduce((acc, p) => acc + p.amountPaid, 0)}
              </Typography>

              <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
                {filteredPayments.map((payment) => (
                  <Box
                    key={payment._id}
                    sx={{
                      mb: 1,
                      p: 1,
                      bgcolor: "#fff",
                      borderRadius: "5px",
                    }}
                  >
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography level="body-sm" sx={{ fontWeight: "bold" }}>
                        {payment.customerId?.name} -{" "}
                        {
                          userData?.schemes?.find(
                            (s) => s._id === payment.schemeId?.schemeId
                          )?.schemeName
                        }
                      </Typography>
                      <Typography level="body-xs" sx={{ color: "#800001" }}>
                        ₹{payment.amountPaid}
                      </Typography>
                    </Box>

                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography level="body-sm">
                        Remarks: {payment.remarks || "—"}
                      </Typography>
                      <Typography level="body-xs" color="neutral">
                        {payment.createdAt?.split("T")[0]}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </>
          ) : (
            <Typography level="body-sm" color="neutral" sx={{ mt: 1 }}>
              No payments in this range.
            </Typography>
          )}

          {/* ================= CUSTOMER UPDATES ================= */}
          <Divider sx={{ my: 2 }} />

          <Typography level="h5" sx={{ fontWeight: "bold", mb: 1 }}>
            Customer Updates
          </Typography>

          {customerUpdates.length > 0 ? (
            <Box sx={{ maxHeight: 250, overflowY: "auto" }}>
              {customerUpdates.map((u, idx) => (
                <Box
                  key={idx}
                  sx={{ mb: 1, p: 1, bgcolor: "#fff", borderRadius: "5px" }}
                >
                  <Typography level="body-sm" sx={{ fontWeight: "bold" }}>
                    {u.customerName}
                  </Typography>
                  <Typography level="body-sm">Update: {u.text}</Typography>
                  <Typography level="body-xs">
                    By: {u.addedBy || "Unknown"} | {u.date?.split("T")[0]}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography level="body-sm" color="neutral">
              No updates for customers in this range.
            </Typography>
          )}

          {/* ================= DESIGN UPDATES ================= */}
          <Divider sx={{ my: 2 }} />

          <Typography level="h5" sx={{ fontWeight: "bold", mb: 1 }}>
            Design Updates
          </Typography>

          {designUpdates.length > 0 ? (
            <Box sx={{ maxHeight: 250, overflowY: "auto" }}>
              {designUpdates.map((u, idx) => (
                <Box
                  key={idx}
                  sx={{ mb: 1, p: 1, bgcolor: "#fff", borderRadius: "5px" }}
                >
                  <Typography level="body-sm" sx={{ fontWeight: "bold" }}>
                    {u.designName} – {u.assetName}
                  </Typography>
                  <Typography level="body-sm">Update: {u.text}</Typography>
                  <Typography level="body-xs">
                    By: {u.addedBy || "Unknown"} | {u.date?.split("T")[0]}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography level="body-sm" color="neutral">
              No design updates in this range.
            </Typography>
          )}
        </Sheet>
      </Modal>
    </Box>
  );
}
